<template>
    <modal :id="modalId" :title="title" :ok-title="okTitle" @show="handleShown" @hide="handleHidden">
        <busy-modal :show="loading"></busy-modal>
        <div v-if="reasons && !!reasons.length">
            <p v-if="shouldConfirm">
                <template v-if="action === 'unlist'">
                    Are you sure you want to unlist the property? Your listing won't appear in search results and can't
                    be booked on Airbnb. Airbnb requires that we ask why the property is being "unlisted". Please click
                    proceed to answer those questions and complete the unlisting process.
                </template>
                <template v-else>
                    This property is currently integrated / sync'd to Airbnb.
                    {{ action === 'disable' ? 'Disabling' : 'Deleting' }} the property will result in Airbnb “Unlisting”
                    the property. While the listing will remain in your Airbnb account, guests will no longer be able to
                    book or find the property on Airbnb. Airbnb requires that we ask why the property is being
                    “Unlisted”. Please click proceed to answer those questions and complete the property disablement
                    process.
                </template>
            </p>
            <div v-else>
                <div v-if="selectedReason === 'OtherReasons' || selectedSubReason === 'OtherReason'">
                    <form-textarea
                        autofocus
                        v-model="reasonDetails"
                        :label="reasonDetailsLabel"
                        :error="errors.reasonDetails"
                        @input="(val) => setFieldValue('reasonDetails', val)"
                    />
                </div>
                <div v-else-if="!selectedReason || subReasons.length === 0">
                    <p>Let Airbnb know why you want to unlist your property.</p>
                    <b-form-radio
                        v-for="reason in reasons"
                        v-model="selectedReason"
                        name="reason"
                        :value="reason.value"
                        :key="reason.value"
                        class="mt-md-0 mt-3"
                        :error="errors.selectedReason"
                        @input="(val) => setFieldValue('selectedReason', val)"
                    >
                        {{ reason.description }}
                    </b-form-radio>
                </div>
                <div v-else>
                    <b-form-radio
                        v-for="subReason in subReasons"
                        v-model="selectedSubReason"
                        name="sub-reason"
                        :value="subReason.value"
                        :key="subReason.value"
                        class="mt-md-0 mt-3"
                        :error="errors.selectedSubReason"
                        @input="(val) => setFieldValue('selectedSubReason', val)"
                    >
                        {{ subReason.description }}
                    </b-form-radio>
                </div>
            </div>
        </div>
        <div v-else-if="reasons">
            <p>
                Are you sure you want to
                {{ action === 'disable' ? 'disable' : action === 'unlist' ? 'unlist' : 'permanently delete' }} this
                property?
            </p>
        </div>
        <template #modal-footer="{ cancel, ok }">
            <div>
                <b-button class="mr-2" variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button class="mr-2" v-if="selectedReason" variant="default" @click="handleBack">Back</b-button>
                <b-button variant="primary" :disabled="!meta.valid" @click="handleOk">{{ okTitle }}</b-button>
            </div>
        </template>
    </modal>
</template>

<script>
    import { ref, computed, defineComponent } from 'vue';
    import API from '../../api';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    import Modal from '../generic/modal/modal.vue';
    import { useForm } from 'vee-validate';

    export default defineComponent({
        name: 'DeActivationModal',
        components: {
            BusyModal,
            FormTextarea,
            Modal,
        },
        props: {
            propertyId: {
                type: Number,
                required: true,
            },
            action: {
                type: String,
                required: true,
            },
            modalId: {
                type: String,
                required: true,
            },
        },
        setup(props, { emit }) {
            // Reactive state
            const loading = ref(false);
            const reasons = ref([]);
            const selectedReason = ref(null);
            const selectedSubReason = ref(null);
            const reasonDetails = ref('');
            const confirmed = ref(false);

            const shouldConfirm = computed(() => {
                return reasons.value && !!reasons.value.length && !confirmed.value;
            });

            const schema = computed(() => {
                return reasons.value.length
                    ? {
                          reasonDetails:
                              selectedReason.value === 'OtherReasons' || selectedSubReason.value === 'OtherReason'
                                  ? 'required'
                                  : '',
                          selectedReason: !shouldConfirm.value && 'required',
                          selectedSubReason:
                              selectedReason.value && !['OtherReasons', 'OtherReason'].includes(selectedReason.value)
                                  ? 'required'
                                  : '',
                      }
                    : {};
            });

            const { setFieldValue, meta, errors } = useForm({
                validationSchema: schema,
            });

            const setValue = (field, value) => {
                setFieldValue(field, value);
            };

            // Computed properties
            const title = computed(() => {
                if (loading.value) {
                    return '';
                } else if (shouldConfirm.value) {
                    return 'Please confirm';
                } else if (props.action === 'disable') {
                    return 'Disable Property';
                } else if (props.action === 'unlist') {
                    return 'Unlist Property';
                } else {
                    return 'Permanently Delete Property';
                }
            });

            const okTitle = computed(() => {
                if (loading.value) {
                    return '...';
                } else if (shouldConfirm.value) {
                    return 'Proceed';
                } else if (props.action === 'disable') {
                    return 'Disable';
                } else if (props.action === 'unlist') {
                    return 'Unlist';
                } else {
                    return 'Delete';
                }
            });

            const reasonDetailsLabel = computed(() => {
                return `Tell Airbnb more about why you want to unlist the property:`;
            });

            const subReasons = computed(() => {
                if (!selectedReason.value) {
                    return [];
                }

                const reason = reasons.value.find((reason) => reason.value === selectedReason.value);
                if (!reason) {
                    return [];
                }

                return reason.choices;
            });

            // Methods
            const handleShown = async () => {
                await fetchDeactivationReasons();
                emit('shown');
            };

            const handleBack = () => {
                selectedReason.value = null;
                selectedSubReason.value = null;
                reasonDetails.value = '';
                setValue('reasonDetails', '');
                setValue('selectedReason', null);
                setValue('selectedSubReason', null);
            };

            const handleOk = (event) => {
                if (shouldConfirm.value) {
                    event.preventDefault();
                    confirmed.value = true;
                    return;
                }

                emit('ok', {
                    reason: selectedSubReason.value || selectedReason.value,
                    reasonDetails: reasonDetails.value,
                });
            };

            const fetchDeactivationReasons = async () => {
                loading.value = true;
                try {
                    const { data } = await API.get(`/app/property/property/${props.propertyId}/deactivation-reasons/`);
                    reasons.value = data;
                } catch (error) {
                    errorToast({ text: 'Could not load Airbnb deactivation reasons.' });
                    reasons.value = [];
                } finally {
                    loading.value = false;
                }
            };

            const handleHidden = () => {
                reasons.value = [];
                selectedReason.value = null;
                selectedSubReason.value = null;
                reasonDetails.value = '';
                confirmed.value = false;
            };

            return {
                // State
                loading,
                reasons,
                selectedReason,
                selectedSubReason,
                reasonDetails,
                confirmed,

                // Computed
                title,
                okTitle,
                reasonDetailsLabel,
                subReasons,
                shouldConfirm,
                meta,
                errors,

                // Methods
                handleShown,
                handleBack,
                handleOk,
                fetchDeactivationReasons,
                handleHidden,
                setFieldValue: setValue,
            };
        },
    });

    // import API from '../../api';
    // import BusyModal from '../generic/busy-modal/busy-modal.vue';
    // import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    // import Modal from '../generic/modal/modal.vue';

    // export default {
    //     name: 'DeActivationModal',
    //     components: {
    //         BusyModal,
    //         FormTextarea,
    //         Modal,
    //     },
    //     props: {
    //         propertyId: {
    //             type: Number,
    //             required: true,
    //         },
    //         action: {
    //             type: String,
    //             required: true,
    //         },
    //         modalId: {
    //             type: String,
    //             required: true,
    //         },
    //     },
    //     data() {
    //         return {
    //             loading: false,
    //             reasons: null,
    //             selectedReason: null,
    //             selectedSubReason: null,
    //             reasonDetails: '',
    //             confirmed: false,
    //         };
    //     },
    //     computed: {
    //         title() {
    //             if (this.loading) {
    //                 return '';
    //             } else if (this.shouldConfirm) {
    //                 return 'Please confirm';
    //             } else if (this.action === 'disable') {
    //                 return 'Disable Property';
    //             } else if (this.action === 'unlist') {
    //                 return 'Unlist Property';
    //             } else {
    //                 return 'Permanently Delete Property';
    //             }
    //         },
    //         okTitle() {
    //             if (this.loading) {
    //                 return '...';
    //             } else if (this.shouldConfirm) {
    //                 return 'Proceed';
    //             } else if (this.action === 'disable') {
    //                 return 'Disable';
    //             } else if (this.action === 'unlist') {
    //                 return 'Unlist';
    //             } else {
    //                 return 'Delete';
    //             }
    //         },
    //         reasonDetailsLabel() {
    //             return `Tell Airbnb more about why you want to unlist the property:`;
    //         },
    //         subReasons() {
    //             if (!this.selectedReason) {
    //                 return [];
    //             }

    //             const reason = this.reasons.find((reason) => reason.value === this.selectedReason);
    //             if (!reason) {
    //                 return [];
    //             }

    //             return reason.choices;
    //         },
    //         shouldConfirm() {
    //             return this.reasons && !!this.reasons.length && !this.confirmed;
    //         },
    //     },
    //     methods: {
    //         async handleShown() {
    //             await this.fetchDeactivationReasons();
    //             this.$emit('shown');
    //         },
    //         handleBack() {
    //             this.selectedReason = null;
    //             this.selectedSubReason = null;
    //             this.reasonDetails = '';
    //         },
    //         handleOk(event) {
    //             if (this.shouldConfirm) {
    //                 event.preventDefault();
    //                 this.confirmed = true;
    //                 return;
    //             }

    //             this.$emit('ok', {
    //                 reason: this.selectedSubReason || this.selectedReason,
    //                 reasonDetails: this.reasonDetails,
    //             });
    //         },
    //         async fetchDeactivationReasons() {
    //             this.loading = true;
    //             try {
    //                 const { data } = await API.get(`/app/property/property/${this.propertyId}/deactivation-reasons/`);
    //                 this.reasons = data;
    //             } catch (error) {
    //                 this.errorToast({ text: 'Could not load Airbnb deactivation reasons.' });
    //                 this.reasons = [];
    //             } finally {
    //                 this.loading = false;
    //             }
    //         },
    //         handleHidden() {
    //             this.reasons = null;
    //             this.selectedReason = null;
    //             this.selectedSubReason = null;
    //             this.reasonDetails = '';
    //             this.confirmed = false;
    //         },
    //     },
    // };
</script>
